import { useState, useEffect, useCallback, useRef } from 'react';
import { communityApi } from './communityApi';
import { Bell, Check, X } from 'lucide-react';

const NOTIF_ICONS = {
  like_thesis: '❤️',
  comment_thesis: '💬',
  answer_question: '💬',
  perf_verified: '✅',
  badge: '🏆',
  level_up: '⭐',
};

const timeSince = (dateStr) => {
  const seconds = Math.floor((Date.now() - new Date(dateStr).getTime()) / 1000);
  if (seconds < 60) return "à l'instant";
  const mins = Math.floor(seconds / 60);
  if (mins < 60) return `${mins}min`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h`;
  const days = Math.floor(hours / 24);
  return `${days}j`;
};

const NotificationsPanel = () => {
  const [open, setOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const panelRef = useRef(null);

  const loadUnread = useCallback(async () => {
    try {
      const { count } = await communityApi.getUnreadCount();
      setUnreadCount(count);
    } catch { /* */ }
  }, []);

  useEffect(() => {
    loadUnread();
    const interval = setInterval(loadUnread, 30000);
    return () => clearInterval(interval);
  }, [loadUnread]);

  useEffect(() => {
    const handleClick = (e) => {
      if (panelRef.current && !panelRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const handleOpen = async () => {
    if (!open) {
      setLoading(true);
      try {
        setNotifications(await communityApi.getNotifications());
      } catch { /* */ }
      finally { setLoading(false); }
    }
    setOpen(!open);
  };

  const handleMarkAllRead = async () => {
    try {
      await communityApi.markNotificationsRead();
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
      setUnreadCount(0);
    } catch { /* */ }
  };

  return (
    <div className="relative" ref={panelRef} data-testid="notifications-panel">
      {/* Bell button */}
      <button onClick={handleOpen} className="relative p-2 rounded-lg hover:bg-white/5 transition-colors" data-testid="notifications-bell">
        <Bell className={`w-5 h-5 ${unreadCount > 0 ? 'text-[#00e5a0]' : 'text-cap-text-muted'}`} />
        {unreadCount > 0 && (
          <span className="absolute -top-0.5 -right-0.5 w-5 h-5 bg-red-500 rounded-full flex items-center justify-center text-white text-[9px] font-bold" data-testid="notif-badge">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {/* Dropdown panel */}
      {open && (
        <div className="absolute right-0 top-full mt-2 w-80 bg-[#1a1d27] border border-white/10 rounded-xl shadow-2xl z-50 overflow-hidden" data-testid="notif-dropdown">
          <div className="flex items-center justify-between px-4 py-3 border-b border-white/5">
            <h3 className="text-white text-sm font-bold">Notifications</h3>
            <div className="flex items-center gap-2">
              {unreadCount > 0 && (
                <button onClick={handleMarkAllRead} className="text-[#00e5a0] text-[10px] hover:underline flex items-center gap-1" data-testid="mark-all-read">
                  <Check className="w-3 h-3" /> Tout marquer lu
                </button>
              )}
              <button onClick={() => setOpen(false)} className="text-cap-text-muted hover:text-white p-0.5"><X className="w-3.5 h-3.5" /></button>
            </div>
          </div>

          <div className="max-h-80 overflow-y-auto">
            {loading ? (
              <div className="flex justify-center py-6"><div className="w-4 h-4 border-2 border-cap-text-muted border-t-transparent rounded-full animate-spin" /></div>
            ) : notifications.length === 0 ? (
              <p className="text-cap-text-muted text-xs text-center py-6">Aucune notification</p>
            ) : (
              notifications.map(n => (
                <div key={n.id}
                  className={`px-4 py-3 border-b border-white/[0.03] transition-colors hover:bg-white/[0.02] ${!n.read ? 'bg-[#00e5a0]/[0.03]' : ''}`}
                  data-testid={`notif-${n.id}`}>
                  <div className="flex items-start gap-2.5">
                    <span className="text-base mt-0.5">{NOTIF_ICONS[n.type] || '🔔'}</span>
                    <div className="flex-1 min-w-0">
                      <p className={`text-xs leading-relaxed ${!n.read ? 'text-white' : 'text-cap-text-secondary'}`}>{n.message}</p>
                      <p className="text-cap-text-muted text-[10px] mt-0.5">{timeSince(n.created_at)}</p>
                    </div>
                    {!n.read && <div className="w-2 h-2 rounded-full bg-[#00e5a0] flex-shrink-0 mt-1.5" />}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationsPanel;
